import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';
import { FOLLOWING_STATUS } from 'constants/follows/following-constants';
import { followAthlete, unfollowAthlete } from 'api/athlete';
import AthleteInfo from '../AthleteInfo';
import { useFriendsContext } from '../../../state/FriendsContext';
import {
  ADD_ALERT,
  CLEAR_ALERT,
  ADD_ATHLETE,
  REMOVE_ATHLETE,
  UPDATE_FRIEND_FOLLOWING_STATUS
} from '../../../state/friendsActions';
import { t } from '../../../utils/friendsUtils';
import {
  INVITE_FRIENDS,
  SUGGESTED_FRIENDS
} from '../../../utils/friendsConstants';
import useCtaAction from '../../../utils/useCtaAction';

const FriendListItem = ({ id }) => {
  // retrieve friend from shared state
  const { state, dispatch, track } = useFriendsContext();
  const { friends, currentAthlete, source } = state;
  const friend = friends[id];
  const [isProcessing, setIsProcessing] = useState(false);

  const cta = useCtaAction(friend);

  const handleFollowAthlete = () => {
    followAthlete({
      followerId: currentAthlete.id,
      followingId: friend.id
    })
      .then((status) => {
        const analytics = {
          element: source === INVITE_FRIENDS ? `${cta}_btn` : cta,
          action: 'click',
          properties: {
            athlete_id: friend.id,
            following_status: status
          }
        };
        if (source === INVITE_FRIENDS) {
          analytics.properties.current_tab = 'facebook';
        }
        track(analytics);
        if (status === FOLLOWING_STATUS.error) {
          dispatch({
            type: ADD_ALERT,
            payload: {
              alert: { severity: 'error', message: t(`errors.generic`) }
            }
          });
        } else {
          dispatch({
            type: UPDATE_FRIEND_FOLLOWING_STATUS,
            payload: {
              id: friend.id,
              isFollowable: false,
              followingStatus: status
            }
          });
          dispatch({
            type: ADD_ATHLETE,
            payload: { friend }
          });
        }
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const handleUnfollowAthlete = () => {
    unfollowAthlete({
      followerId: currentAthlete.id,
      followingId: friend.id
    })
      .then((success) => {
        track({
          action: 'click',
          element: 'unfollow',
          properties: {
            athlete_id: friend.id,
            success
          }
        });
        if (success) {
          dispatch({
            type: UPDATE_FRIEND_FOLLOWING_STATUS,
            payload: {
              id: friend.id,
              isFollowable: true,
              followingStatus: friend.approveFollowers
                ? FOLLOWING_STATUS.canRequest
                : FOLLOWING_STATUS.canFollow
            }
          });
          dispatch({
            type: REMOVE_ATHLETE,
            payload: {
              id: friend.id
            }
          });
        } else {
          dispatch({
            type: ADD_ALERT,
            payload: {
              alert: { severity: 'error', message: t(`errors.generic`) }
            }
          });
        }
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const handleBtnClick = () => {
    setIsProcessing(true);
    dispatch({ type: CLEAR_ALERT });

    if (friend.isFollowable) {
      handleFollowAthlete();
    } else {
      handleUnfollowAthlete();
    }
  };

  return (
    <AthleteInfo friend={friend}>
      <Button
        data-cy="friend-list-item-btn"
        type="button"
        variant={
          source === SUGGESTED_FRIENDS && !friend.isFollowable
            ? 'default'
            : 'primary'
        }
        onClick={handleBtnClick}
        disabled={
          (source !== SUGGESTED_FRIENDS && !friend.isFollowable) || isProcessing
        }
      >
        {t(`cta_actions.${cta}`)}
      </Button>
    </AthleteInfo>
  );
};

FriendListItem.propTypes = {
  id: PropTypes.number.isRequired
};

export default FriendListItem;
